'use client';

import { useEffect, useMemo, useState } from 'react';

import {
    Alignable,
    Column,
    Columns,
    Container,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TypographyAlign,
    TypographyBold,
} from '@bytel/trilogy-react';

import { ShortListContext } from '@app-types/eligibility';
import { UpsellModalComponentType } from '@app-types/eligibility/config';
import { CategoryCodeInfo } from '@app-types/plan';

import { getUpsellCombination, getUpsellOffer } from '@helpers/fai-upsell';
import { formatTagText, sendPageViewTracking } from '@helpers/tracking';

import { TRACKING_TYPE_SEARCH, TYPE_ELIGIBILITY } from '@constants/eligibility';
import { ELIGIBILITY_JOURNEY, TECHNOLOGY } from '@constants/provider';

import {
    EligibilityOffer,
    EligibilityResults,
    JourneyConfigType,
    sendTrackingAddFaiToCart,
} from '@eligibility/helpers';

import { Forecast } from '../forecast';
import { EditAddress } from './edit-address';
import { EligibilityResultError } from './eligibility-result-error';
import { EligibilityResultSegmentControl } from './eligibility-result-segment-control';
import { EligibilityResultTabs } from './eligibility-result-tabs';

interface EligibilityResultProps {
    type: TYPE_ELIGIBILITY;
    results: EligibilityResults;
    onResetAddress?: () => void;
    journeyConfig: JourneyConfigType;
    addToCart: (gencodes: string[], redirect?: boolean, callback?: () => void) => Promise<void>;
    shortList?: ShortListContext;
    openOfferUpsellModal: (
        clickedOffer: EligibilityOffer,
        upsellOffer: EligibilityOffer,
        modal?: UpsellModalComponentType,
    ) => void;
    categoryCodeInfo: CategoryCodeInfo[];
    planId?: string;
    shouldShowNewFaiCategories: boolean;
}

// Exposed so it can be reused elsewhere on the page
const getTrackingSearchType = (type: TYPE_ELIGIBILITY) => {
    switch (type) {
        case TYPE_ELIGIBILITY.SEARCH:
            return TRACKING_TYPE_SEARCH.ADDRESS;
        case TYPE_ELIGIBILITY.PTO:
            return TRACKING_TYPE_SEARCH.PTO;
        default:
            return undefined;
    }
};

const sendEligibilityResultsTracking = (
    type: TYPE_ELIGIBILITY,
    results: EligibilityResults,
    journeyConfig: JourneyConfigType,
    isNewTabsUI: boolean = false,
) => {
    const tracking = journeyConfig.tracking;

    const eligibleTechnos = Object.entries(results.offers).reduce((acc, [technology, offers]) => {
        if (offers.length > 0) {
            acc.push(technology);
        }
        return acc;
    }, [] as string[]);

    const productsTag = Object.values(results.offers)
        .flat()
        .map((offer) => {
            const offerPrice: string = `${offer.price + (offer.rental_fees || 0) - (offer.promotion?.amount || 0)}`;

            return {
                id: offer.sku,
                name: offer.name,
                gencode: offer.sku,
                sku: offer.sku,
                type_id: offer.type,
                rangeNg: offer.rangeNg,
                commercialSeries: offer.commercialSeries,
                price: +parseFloat(offerPrice).toFixed(2),
                technology: offer.technology,
                network: 'N/A',
                play: offer.play,
            };
        });

    sendPageViewTracking(false, {
        page: 'Eligibilite_resultats',
        pageCategory: tracking.pageCategory,
        eCommerceStep: 'Liste_produits',
        universe: tracking.universe,
        variant: {
            ...tracking?.variant,
            etape_eligibilite: 'Eligibilite_resultats',
            eligibleOffresFAI: (Object.values(results.offers).length > 0).toString(),
            affichageTogglePlay: false,
            onglet: window.location.hash.replace('#', ''),
            abtest: isNewTabsUI.toString(),
        },
        variables: {
            ...tracking?.variables,
            eligibilite_memo_ancien_test: 'false',
            eligibilite_memo_offre: 'false',
            selection_offre_specifique: 'false',
            eligibilite_techno_technique: 'FTTH, ADSL, VDSL, BOX_4G, BOX_5G',
            eligibilite_techno_resultats: eligibleTechnos.join(','),
            eligibiliteConvergence_offresFai: 'Bbox',
            testParAdresse: getTrackingSearchType(type),
        },
        products: productsTag,
    });
};

export function EligibilityResult({
    type,
    results,
    onResetAddress,
    addToCart,
    journeyConfig,
    shortList,
    openOfferUpsellModal,
    categoryCodeInfo,
    planId,
    shouldShowNewFaiCategories,
}: Readonly<EligibilityResultProps>) {
    const [address, setAddress] = useState(results.address.inlineSplit);

    const checkUpsellOffer = async (sku: string, shouldAddToCart: boolean) => {
        const allResultsOffers = Object.values(results.offers).flat();
        const clickedOffer = allResultsOffers.find((offer) => offer.sku === sku)!;
        const upsellOffer = getUpsellOffer(allResultsOffers, clickedOffer, journeyConfig.productsToUpsell);

        if (upsellOffer) {
            const upsellCombination = getUpsellCombination(clickedOffer, journeyConfig.productsToUpsell)!;
            openOfferUpsellModal(clickedOffer, upsellOffer, upsellCombination.modalComponentType);
        } else if (shouldAddToCart) {
            const autoAdd = clickedOffer?.neededElements?.map((el) => el.gencode) ?? [];
            await addToCart([clickedOffer.sku, ...autoAdd], true, () =>
                sendTrackingAddFaiToCart(
                    journeyConfig,
                    `Ajouter_au_panier_${formatTagText(clickedOffer?.name)}`,
                    clickedOffer?.technology === TECHNOLOGY.FTTH,
                ),
            );
        }
    };

    const handleAddToCart = (gencode: string) => {
        if (Boolean(journeyConfig.productsToUpsell)) {
            void checkUpsellOffer(gencode, true);
        } else {
            const allResultsOffers = Object.values(results.offers).flat();
            const offer = allResultsOffers?.find((o) => o.sku === gencode);
            const autoAdd = offer?.neededElements?.map((el) => el.gencode) ?? [];
            void addToCart([gencode, ...autoAdd], true, () =>
                sendTrackingAddFaiToCart(
                    journeyConfig,
                    `Ajouter_au_panier_${formatTagText(offer?.name)}`,
                    offer?.technology === TECHNOLOGY.FTTH,
                ),
            );
        }
    };

    const onTabChange = () => {
        sendEligibilityResultsTracking(type, results, journeyConfig, shouldShowNewFaiCategories);
    };

    const shouldDisplayDefaultMessage = useMemo(() => {
        if (!shortList || shortList.focus) {
            return true;
        }

        // This case appear when the direct to cart fails
        return (
            results.offers[shortList.technology.toUpperCase() as TECHNOLOGY]?.some((offer) => {
                return offer.isSameRangeAndPlay(shortList.rangeNgCommercialSeries, '');
            }) ?? false
        );
    }, [shortList, results]);

    const getAddressByEligType = (type: TYPE_ELIGIBILITY): string[] => {
        const address = [];
        if (type === TYPE_ELIGIBILITY.PTO) {
            address.push(results.address.pto!);
        }

        if (address.length && !!results.address.inline.trim()) {
            address.push('|');
        }

        return address.concat(results.address.inlineSplit);
    };

    useEffect(() => {
        sendEligibilityResultsTracking(type, results, journeyConfig, shouldShowNewFaiCategories);
        setAddress(getAddressByEligType(type));
    }, [type, results, journeyConfig]);

    const hasFallbackOffers = Object.values(results.offers).some((offers) =>
        offers.some((offer) => offer.isFallbackMismatchOffer),
    );

    const has5gOffer = results.results?.box5g.offers?.length > 0;

    return (
        <>
            <Columns align={Alignable.ALIGNED_CENTER}>
                <Column>
                    {!results.error ? (
                        <Rows gap={2}>
                            {shouldDisplayDefaultMessage ? null : (
                                <Row>
                                    <Title level={TitleLevels.FOUR} className='has-text-centered'>
                                        La {shortList!.name}
                                        {shortList!.technology.toUpperCase() === TECHNOLOGY.FTTH
                                            ? ' avec la Fibre '
                                            : ' '}
                                        n&apos;est pas disponible chez vous, mais vous avez accès à d&apos;autres offres
                                        &nbsp;!
                                    </Title>
                                </Row>
                            )}
                            <Row>
                                <EditAddress onResetAddress={onResetAddress} address={address} />
                                <Spacer size={SpacerSize.FIVE} />
                                {results.technology &&
                                    [TECHNOLOGY.BOX_4G, TECHNOLOGY.BOX_5G].includes(results.technology) && (
                                        <Text
                                            level={TextLevels.ONE}
                                            typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                                        >
                                            Vous pouvez profiter de la {has5gOffer ? '5G' : '4G'} box{' '}
                                            {has5gOffer ? 'très' : ''} haut débit
                                        </Text>
                                    )}
                            </Row>
                        </Rows>
                    ) : (
                        <EligibilityResultError
                            results={results}
                            error={results.error}
                            address={
                                <>
                                    <EditAddress onResetAddress={onResetAddress} address={address} />
                                    <Spacer size={SpacerSize.EIGHT} />
                                </>
                            }
                            planId={planId}
                            onAddToCart={handleAddToCart}
                            journeyConfig={journeyConfig}
                        />
                    )}
                </Column>
            </Columns>
            <Columns align={Alignable.ALIGNED_CENTER}>
                <Column desktopSize={10}>
                    {shouldShowNewFaiCategories && journeyConfig.name === ELIGIBILITY_JOURNEY.DEFAULT ? (
                        <EligibilityResultSegmentControl
                            journeyConfig={journeyConfig}
                            results={results}
                            shortList={shortList}
                            categoryCodeInfo={categoryCodeInfo}
                            onAddToCart={handleAddToCart}
                            onTabChange={onTabChange}
                        />
                    ) : (
                        <EligibilityResultTabs
                            journeyConfig={journeyConfig}
                            results={results}
                            shortList={shortList}
                            categoryCodeInfo={categoryCodeInfo}
                            onAddToCart={handleAddToCart}
                        />
                    )}
                </Column>
            </Columns>
            {!hasFallbackOffers && Object.values(results.offers).length > 0 && (
                <Container medium>
                    <Forecast results={results} journeyConfig={journeyConfig} />
                    <Spacer size={SpacerSize.FOUR} />
                </Container>
            )}
        </>
    );
}
